import React, { useState, useEffect, useRef, useCallback } from 'react';
import { knowledgeAPI } from '../../services/api';
import toast from 'react-hot-toast';

/* ── Constants ──────────────────────────────────────────────── */
const SUGGESTED_TAGS = ['brochure','pricing','objections','curriculum','internship','franchise','corporate','testimonials','faq','mou'];
const USE_FOR_OPTIONS = [
  { value: 'marketing',         label: 'Marketing Content',  icon: '📣' },
  { value: 'objections',        label: 'Objection Handling', icon: '🛡️' },
  { value: 'emails',            label: 'Email Writing',      icon: '✉️' },
  { value: 'proposals',         label: 'Proposal Writing',   icon: '📝' },
  { value: 'ai_analyzer',       label: 'AI Analyzer',        icon: '🤖' },
];
const STATUS_COLORS = {
  uploaded:  { bg: '#f1f5f9', color: '#64748b', label: 'Uploaded' },
  indexing:  { bg: '#fef9c3', color: '#b45309', label: 'Indexing' },
  indexed:   { bg: '#dcfce7', color: '#16a34a', label: 'Indexed'  },
  failed:    { bg: '#fee2e2', color: '#dc2626', label: 'Failed'   },
};
const TYPE_COLORS = { pdf: '#ef4444', docx: '#3b82f6', txt: '#8b5cf6' };

/* ── Utility ────────────────────────────────────────────────── */
const fmt = n => n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
const fmtDate = d => d ? new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Not yet';

/* ── Spinner ────────────────────────────────────────────────── */
const Spin = ({ size = 14 }) => (
  <span style={{
    display: 'inline-block', width: size, height: size,
    border: `2px solid currentColor`, borderTopColor: 'transparent',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0,
  }} />
);

export default function KnowledgeBasePage() {
  /* ── Document list state ── */
  const [documents,   setDocuments]   = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter,setStatusFilter]= useState('all');
  const [useForFilter,setUseForFilter]= useState('all');
  const [selectedDocs,setSelectedDocs]= useState([]);
  const [deleteModal,    setDeleteModal]    = useState(null);  // doc object
  const [reindexModal,   setReindexModal]   = useState(false);
  const [indexModal,     setIndexModal]     = useState(false);
  const [pasteTextModal, setPasteTextModal] = useState(null); // doc object
  const [pasteText,      setPasteText]      = useState('');
  const indexPollRef = useRef(null);

  /* ── Upload state ── */
  const [queue,        setQueue]       = useState([]);       // { file, id }
  const [activeIdx,    setActiveIdx]   = useState(0);
  const [metadata,     setMetadata]    = useState({ title: '', description: '', tags: [], use_for: [] });
  const [tagInput,     setTagInput]    = useState('');
  const [dragging,     setDragging]    = useState(false);
  const [uploading,    setUploading]   = useState(false);
  const [uploadDone,   setUploadDone]  = useState(false);
  const fileInputRef = useRef();
  const pollRef      = useRef(null);

  /* ── Load documents ── */
  const loadDocuments = useCallback(async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        knowledgeAPI.getDocuments(),
        knowledgeAPI.getStats(),
      ]);
      setDocuments(docsRes.data?.data?.documents || []);
      setStats(statsRes.data?.data || null);
    } catch (err) {
      console.error('Failed to load knowledge documents:', err);
      toast.error('Failed to load documents. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  /* ── Cleanup index poll on unmount ── */
  useEffect(() => () => { if (indexPollRef.current) clearInterval(indexPollRef.current); }, []);

  /* ── Auto-poll while indexing ── */
  useEffect(() => {
    const hasIndexing = documents.some(d => d.status === 'indexing');
    if (hasIndexing) {
      if (!pollRef.current) {
        pollRef.current = setInterval(loadDocuments, 5000);
      }
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [documents, loadDocuments]);

  /* ── Pre-fill title when active file changes ── */
  useEffect(() => {
    if (queue.length > 0 && activeIdx < queue.length) {
      const name = queue[activeIdx].file.name.replace(/\.[^.]+$/, '');
      setMetadata({ title: name, description: '', tags: [], use_for: [] });
    }
  }, [activeIdx, queue]);

  /* ── File handling ── */
  const addFiles = files => {
    const valid = Array.from(files).filter(f => /\.(pdf|docx|txt)$/i.test(f.name) && f.size <= 20 * 1024 * 1024);
    if (valid.length < files.length) toast.error('Only PDF, DOCX, TXT ≤ 20 MB allowed');
    setQueue(q => [...q, ...valid.map(file => ({ file, id: `${Date.now()}-${Math.random()}` }))]);
    setUploadDone(false);
  };

  const removeFromQueue = idx => {
    setQueue(q => q.filter((_, i) => i !== idx));
    if (queue.length === 1) {
      setActiveIdx(0);
    } else if (activeIdx >= idx && activeIdx > 0) {
      setActiveIdx(i => i - 1);
    }
  };

  /* ── Tags ── */
  const addTag = tag => {
    const t = tag.trim().toLowerCase();
    if (t && !metadata.tags.includes(t)) setMetadata(m => ({ ...m, tags: [...m.tags, t] }));
    setTagInput('');
  };
  const removeTag = tag => setMetadata(m => ({ ...m, tags: m.tags.filter(t => t !== tag) }));
  const toggleUseFor = val => setMetadata(m => ({
    ...m,
    use_for: m.use_for.includes(val) ? m.use_for.filter(v => v !== val) : [...m.use_for, val],
  }));

  /* ── Upload ── */
  const handleUpload = async () => {
    if (!queue.length || activeIdx >= queue.length) return;
    if (!metadata.title.trim()) { toast.error('Title is required'); return; }

    setUploading(true);
    const { file } = queue[activeIdx];
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', metadata.title);
    fd.append('description', metadata.description);
    fd.append('tags', JSON.stringify(metadata.tags));
    fd.append('use_for', JSON.stringify(metadata.use_for));

    try {
      const uploadRes = await knowledgeAPI.upload(fd);
      const uploadMsg = uploadRes.data?.message || '';
      const uploadedDoc = uploadRes.data?.data?.document;
      if (uploadedDoc?.status === 'failed') {
        toast.error(`Upload issue: ${uploadMsg}`, { duration: 8000 });
      } else {
        toast.success('Uploaded successfully. Select the document and click ⚡ Index to add it to the knowledge base.');
      }
      setUploadDone(true);
      setQueue(q => {
        const next = q.filter((_, i) => i !== activeIdx);
        if (next.length > 0) setActiveIdx(0);
        return next;
      });
      setMetadata({ title: '', description: '', tags: [], use_for: [] });
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /* ── Reindex single ── */
  const handleReindex = async doc => {
    try {
      await knowledgeAPI.reindexDocument(doc._id);
      toast.success(`Reindexing "${doc.title}"…`);
      loadDocuments();
    } catch { toast.error('Reindex failed'); }
  };

  /* ── Reindex multiple ── */
  const handleReindexSelected = async () => {
    try {
      await knowledgeAPI.reindexMultiple(selectedDocs);
      toast.success(`Reindexing started for ${selectedDocs.length} documents`);
      setSelectedDocs([]);
      setReindexModal(false);
      loadDocuments();
    } catch { toast.error('Reindex failed'); }
  };

  /* ── Index selected (new uploaded/failed docs) ── */
  const indexableSelected = selectedDocs.filter(id => {
    const doc = documents.find(d => d._id === id);
    return doc && (doc.status === 'uploaded' || doc.status === 'failed');
  });
  const reindexableSelected = selectedDocs.filter(id => {
    const doc = documents.find(d => d._id === id);
    return doc && doc.status === 'indexed';
  });

  const handleIndexSelected = async () => {
    try {
      await knowledgeAPI.indexSelected(indexableSelected);
      /* Optimistically set status to indexing in local state */
      setDocuments(prev => prev.map(d =>
        indexableSelected.includes(d._id) ? { ...d, status: 'indexing' } : d
      ));
      toast.success(`Indexing started for ${indexableSelected.length} document${indexableSelected.length !== 1 ? 's' : ''}`);
      setIndexModal(false);

      /* Poll until all finish or 10-minute timeout */
      const POLL_INTERVAL = 5000;
      const MAX_POLLS     = 120; // 10 minutes
      let   polls         = 0;

      if (indexPollRef.current) clearInterval(indexPollRef.current);
      indexPollRef.current = setInterval(async () => {
        polls++;
        try {
          const res = await knowledgeAPI.getStatus(indexableSelected);
          const statuses = res.data?.data?.documents || [];
          setDocuments(prev => prev.map(d => {
            const updated = statuses.find(s => s._id === d._id);
            return updated ? { ...d, ...updated } : d;
          }));
          const allDone = statuses.every(s => s.status === 'indexed' || s.status === 'failed');
          if (allDone || polls >= MAX_POLLS) {
            clearInterval(indexPollRef.current);
            indexPollRef.current = null;
            if (allDone) {
              const failedDocs = statuses.filter(s => s.status === 'failed');
              if (failedDocs.length > 0) {
                const errorMsg = failedDocs.map(s => s.index_error).filter(Boolean).join(' | ');
                toast.error(`Indexing failed: ${errorMsg || 'Unknown error — check server logs'}`, { duration: 8000 });
              } else {
                toast.success('Indexing complete. All documents are ready.');
              }
            } else {
              toast('Indexing is taking longer than expected. Check status manually.', { icon: '⚠️' });
            }
            loadDocuments();
          }
        } catch {
          /* network blip — keep polling */
        }
      }, POLL_INTERVAL);
    } catch { toast.error('Failed to start indexing'); }
  };

  /* ── Paste text manually for failed docs ── */
  const handlePasteText = async () => {
    if (!pasteTextModal) return;
    if (!pasteText.trim()) { toast.error('Please paste some text'); return; }
    try {
      await knowledgeAPI.updateDocument(pasteTextModal._id, { extracted_text: pasteText.trim() });
      toast.success('Text saved. Select the document and click ⚡ Index to add it to the knowledge base.');
      setPasteTextModal(null);
      setPasteText('');
      loadDocuments();
    } catch { toast.error('Failed to save text'); }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await knowledgeAPI.deleteDocument(deleteModal._id);
      toast.success(`"${deleteModal.title}" deleted`);
      setDocuments(d => d.filter(x => x._id !== deleteModal._id));
      setSelectedDocs(s => s.filter(id => id !== deleteModal._id));
      setDeleteModal(null);
    } catch { toast.error('Delete failed'); }
  };

  /* ── Filtered documents ── */
  const filtered = documents.filter(doc => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || doc.title?.toLowerCase().includes(q) || doc.original_filename?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || doc.status === statusFilter;
    const matchUseFor = useForFilter === 'all' || doc.use_for?.includes(useForFilter);
    return matchSearch && matchStatus && matchUseFor;
  });

  const allSelected = filtered.length > 0 && filtered.every(d => selectedDocs.includes(d._id));
  const toggleAll = () => setSelectedDocs(allSelected ? [] : filtered.map(d => d._id));
  const toggleDoc = id => setSelectedDocs(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  /* ── Drag & drop handlers ── */
  const onDragOver  = e => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop      = e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };

  /* ── Stats ── */
  const totalIndexed = documents.filter(d => d.status === 'indexed').length;
  const totalChunks  = documents.reduce((s, d) => s + (d.chunk_count || 0), 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .kb-row:hover { background: #f8fafc !important; }
        .kb-tag { display:inline-flex; align-items:center; gap:4px; background:#e0e7ff; color:#3730a3;
          borderRadius:999px; padding:2px 8px; fontSize:11px; fontWeight:600; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>🧠 Knowledge Base</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          Upload documents to power AI content generation, objection handling, and email writing
        </p>
      </div>

      {/* ═══════════════ SECTION 1: UPLOAD ═══════════════ */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

        {/* Drop zone */}
        <div
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#C9A84C' : '#1B2A6B'}`,
            borderRadius: 12, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: dragging ? '#fefce8' : '#f8faff', transition: 'all 0.2s', marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#1B2A6B', margin: '0 0 4px' }}>
            Drag and drop PDF, DOCX, or TXT files here
          </p>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>Max 20 MB per file</p>
          <button
            onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
            style={{ background: '#1B2A6B', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >or Browse Files</button>
          <input
            ref={fileInputRef} type="file" multiple hidden
            accept=".pdf,.docx,.txt"
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {/* Queue list */}
        {queue.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {queue.map(({ file, id }, idx) => (
              <div key={id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: idx === activeIdx ? '#eff6ff' : '#f8fafc', borderRadius: 8, marginBottom: 4,
                border: idx === activeIdx ? '1.5px solid #3b82f6' : '1px solid #e2e8f0', cursor: 'pointer',
              }} onClick={() => setActiveIdx(idx)}>
                <span style={{ fontSize: 18 }}>{/pdf$/i.test(file.name) ? '📕' : /docx$/i.test(file.name) ? '📘' : '📄'}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{fmt(file.size)}</span>
                <button onClick={e => { e.stopPropagation(); removeFromQueue(idx); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Metadata form */}
        {queue.length > 0 && activeIdx < queue.length && (
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Configure: {queue[activeIdx]?.file?.name}
            </p>
            <div style={{ display: 'grid', gap: 14 }}>

              {/* Title */}
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={metadata.title} onChange={e => setMetadata(m => ({ ...m, title: e.target.value }))}
                  style={inputStyle} placeholder="Document title" />
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={metadata.description} onChange={e => setMetadata(m => ({ ...m, description: e.target.value }))}
                  style={{ ...inputStyle, height: 60, resize: 'vertical' }} placeholder="Optional description" />
              </div>

              {/* Tags */}
              <div>
                <label style={labelStyle}>Tags</label>
                {/* Suggested */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {SUGGESTED_TAGS.map(t => (
                    <button key={t} onClick={() => addTag(t)}
                      style={{ background: metadata.tags.includes(t) ? '#1B2A6B' : '#f1f5f9', color: metadata.tags.includes(t) ? '#fff' : '#475569',
                        border: 'none', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      {t}
                    </button>
                  ))}
                </div>
                {/* Tag input */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                    style={{ ...inputStyle, flex: 1 }} placeholder="Type and press Enter to add custom tag" />
                </div>
                {/* Selected tags */}
                {metadata.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {metadata.tags.map(t => (
                      <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                        {t}
                        <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Use For */}
              <div>
                <label style={labelStyle}>Use For</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {USE_FOR_OPTIONS.map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#374151',
                      background: metadata.use_for.includes(opt.value) ? '#eff6ff' : '#f8fafc',
                      border: `1.5px solid ${metadata.use_for.includes(opt.value) ? '#3b82f6' : '#e2e8f0'}`,
                      borderRadius: 8, padding: '6px 12px' }}>
                      <input type="checkbox" checked={metadata.use_for.includes(opt.value)}
                        onChange={() => toggleUseFor(opt.value)} style={{ accentColor: '#1B2A6B', width: 14, height: 14 }} />
                      <span>{opt.icon}</span><span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Upload button */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button onClick={handleUpload} disabled={uploading}
                  style={{ background: uploading ? '#94a3b8' : '#1B2A6B', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {uploading && <Spin />}
                  {uploading ? 'Uploading…' : 'Upload Document'}
                </button>
                {uploadDone && (
                  <span style={{ fontSize: 13, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    ✓ Uploaded. Select below and click ⚡ Index Selected to begin indexing.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════ SECTION 2: DOCUMENT LIST ═══════════════ */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

        {/* List header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', margin: 0, flex: '0 0 auto' }}>
            Indexed Documents
          </h2>
          <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
            {documents.length}
          </span>
          <div style={{ flex: 1, minWidth: 0 }} />

          {/* Toolbar */}
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search documents…"
            style={{ ...inputStyle, width: 180, padding: '6px 10px', fontSize: 13 }} />

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle, fontSize: 12 }}>
            <option value="all">All Status</option>
            {Object.entries(STATUS_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          <select value={useForFilter} onChange={e => setUseForFilter(e.target.value)} style={{ ...selectStyle, fontSize: 12 }}>
            <option value="all">All Uses</option>
            {USE_FOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <button
            onClick={() => setIndexModal(true)}
            disabled={indexableSelected.length === 0}
            title="Index uploaded or failed documents"
            style={{ background: indexableSelected.length ? '#C9A84C' : '#f1f5f9', color: indexableSelected.length ? '#1B2A6B' : '#94a3b8',
              border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: indexableSelected.length ? 'pointer' : 'not-allowed' }}>
            ⚡ Index Selected {indexableSelected.length > 0 ? `(${indexableSelected.length})` : ''}
          </button>
          <button
            onClick={() => setReindexModal(true)}
            disabled={reindexableSelected.length === 0}
            title="Re-process already-indexed documents"
            style={{ background: 'transparent', color: reindexableSelected.length ? '#475569' : '#cbd5e1',
              border: `1.5px solid ${reindexableSelected.length ? '#cbd5e1' : '#e2e8f0'}`, borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: reindexableSelected.length ? 'pointer' : 'not-allowed' }}>
            🔄 Reindex Selected {reindexableSelected.length > 0 ? `(${reindexableSelected.length})` : ''}
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <Spin size={24} /> <span style={{ marginLeft: 10 }}>Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
            <p style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
              {documents.length === 0 ? 'No documents uploaded yet.' : 'No documents match your filters.'}
            </p>
            {documents.length === 0 && <p style={{ fontSize: 13 }}>Upload your first document above to power AI features.</p>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={th}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: '#1B2A6B' }} /></th>
                  <th style={{ ...th, textAlign: 'left' }}>Document</th>
                  <th style={th}>Type</th>
                  <th style={{ ...th, textAlign: 'left' }}>Tags</th>
                  <th style={th}>Use For</th>
                  <th style={th}>Chunks</th>
                  <th style={th}>Status</th>
                  <th style={th}>Indexed At</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(doc => {
                  const s = STATUS_COLORS[doc.status] || STATUS_COLORS.uploaded;
                  const isSelected = selectedDocs.includes(doc._id);
                  return (
                    <tr key={doc._id} className="kb-row"
                      style={{ borderTop: '1px solid #f1f5f9', background: isSelected ? '#eff6ff' : '#fff' }}>
                      {/* Checkbox */}
                      <td style={{ ...td, width: 40, textAlign: 'center' }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleDoc(doc._id)} style={{ accentColor: '#1B2A6B' }} />
                      </td>
                      {/* Title */}
                      <td style={{ ...td, maxWidth: 200 }}>
                        <div style={{ fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_filename}</div>
                      </td>
                      {/* Type */}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ background: TYPE_COLORS[doc.file_type] || '#64748b', color: '#fff', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                          {doc.file_type}
                        </span>
                      </td>
                      {/* Tags */}
                      <td style={{ ...td, maxWidth: 160 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {(doc.tags || []).slice(0, 3).map(t => (
                            <span key={t} style={{ background: '#f1f5f9', color: '#475569', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>{t}</span>
                          ))}
                          {(doc.tags || []).length > 3 && (
                            <span style={{ color: '#94a3b8', fontSize: 10 }}>+{doc.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      {/* Use For */}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {(doc.use_for || []).map(u => {
                            const opt = USE_FOR_OPTIONS.find(o => o.value === u);
                            return opt ? <span key={u} title={opt.label} style={{ fontSize: 14 }}>{opt.icon}</span> : null;
                          })}
                        </div>
                      </td>
                      {/* Chunks */}
                      <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#1B2A6B' }}>
                        {doc.chunk_count || 0}
                      </td>
                      {/* Status */}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span
                          title={doc.status === 'failed' && doc.index_error ? doc.index_error : undefined}
                          style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: doc.status === 'failed' ? 'help' : 'default' }}>
                          {doc.status === 'indexing' && <Spin size={10} />}
                          {doc.status === 'indexed'  && '✓ '}
                          {doc.status === 'failed'   && '⚠ '}
                          {s.label}
                        </span>
                        {doc.status === 'failed' && doc.index_error && (
                          <div style={{ fontSize: 10, color: '#dc2626', marginTop: 3, maxWidth: 160, wordBreak: 'break-word', lineHeight: 1.3 }}>
                            {doc.index_error.length > 80 ? doc.index_error.slice(0, 80) + '…' : doc.index_error}
                          </div>
                        )}
                      </td>
                      {/* Indexed At */}
                      <td style={{ ...td, textAlign: 'center', color: '#64748b', fontSize: 11 }}>
                        {fmtDate(doc.indexed_at)}
                      </td>
                      {/* Actions */}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {doc.status === 'failed' && (
                            <button
                              onClick={() => { setPasteTextModal(doc); setPasteText(''); }}
                              title="Paste text manually (for scanned or unreadable files)"
                              style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                              ✏️
                            </button>
                          )}
                          <button onClick={() => handleReindex(doc)} title="Reindex"
                            style={{ background: '#fef9c3', color: '#b45309', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                            🔄
                          </button>
                          <button onClick={() => setDeleteModal(doc)} title="Delete"
                            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══════════════ STATS BAR ═══════════════ */}
      <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
        {[
          ['📄', 'Total Documents', documents.length],
          ['✅', 'Total Indexed', totalIndexed],
          ['🧩', 'Total Chunks in Qdrant', totalChunks],
          ['🕒', 'Last Updated', stats ? fmtDate(new Date()) : '—'],
        ].map(([icon, label, val]) => (
          <div key={label} style={{ flex: '1 1 180px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{icon}</span>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1B2A6B' }}>{val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══════════════ INDEX MODAL ═══════════════ */}
      {indexModal && (
        <Modal onClose={() => setIndexModal(false)}>
          <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>⚡</div>
          <h3 style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Index Selected Documents</h3>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 24 }}>
            Start indexing <strong>{indexableSelected.length} document{indexableSelected.length !== 1 ? 's' : ''}</strong>? This may take a few minutes per document.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setIndexModal(false)} style={ghostBtnStyle}>Cancel</button>
            <button onClick={handleIndexSelected} style={{ ...primaryBtnStyle, background: '#C9A84C', color: '#1B2A6B' }}>Start Indexing</button>
          </div>
        </Modal>
      )}

      {/* ═══════════════ DELETE MODAL ═══════════════ */}
      {deleteModal && (
        <Modal onClose={() => setDeleteModal(null)}>
          <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🗑️</div>
          <h3 style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Delete Document</h3>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 24 }}>
            Delete <strong>"{deleteModal.title}"</strong>? This will remove the document and all its vectors from the knowledge base.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setDeleteModal(null)} style={ghostBtnStyle}>Cancel</button>
            <button onClick={handleDelete} style={{ ...primaryBtnStyle, background: '#ef4444' }}>Delete</button>
          </div>
        </Modal>
      )}

      {/* ═══════════════ PASTE TEXT MODAL ═══════════════ */}
      {pasteTextModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9990, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) { setPasteTextModal(null); setPasteText(''); } }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 600, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>✏️ Paste Document Text</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
              <strong>"{pasteTextModal.title}"</strong> — automatic text extraction failed.
            </p>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
              Open the document, select all text (Ctrl+A), copy it, and paste below. Once saved, you can index it normally.
            </p>
            <textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder="Paste the document text here…"
              style={{ ...inputStyle, minHeight: 220, resize: 'vertical', fontSize: 13, lineHeight: 1.6 }}
              autoFocus
            />
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 16px' }}>{pasteText.length.toLocaleString()} characters</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setPasteTextModal(null); setPasteText(''); }} style={ghostBtnStyle}>Cancel</button>
              <button onClick={handlePasteText} disabled={!pasteText.trim()} style={{ ...primaryBtnStyle, background: pasteText.trim() ? '#7c3aed' : '#94a3b8', cursor: pasteText.trim() ? 'pointer' : 'not-allowed' }}>
                Save Text &amp; Make Indexable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ REINDEX MODAL ═══════════════ */}
      {reindexModal && (
        <Modal onClose={() => setReindexModal(false)}>
          <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🔄</div>
          <h3 style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Reindex Documents</h3>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 24 }}>
            Reindex <strong>{selectedDocs.length} selected document{selectedDocs.length > 1 ? 's' : ''}</strong>? This will re-process and re-embed all selected files.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setReindexModal(false)} style={ghostBtnStyle}>Cancel</button>
            <button onClick={handleReindexSelected} style={primaryBtnStyle}>Confirm Reindex</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Modal wrapper ──────────────────────────────────────────── */
function Modal({ children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9990, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
        {children}
      </div>
    </div>
  );
}

/* ── Shared styles ──────────────────────────────────────────── */
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 };
const inputStyle  = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const selectStyle = { padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer' };
const th = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#64748b', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
const primaryBtnStyle = { flex: 1, padding: '11px 0', background: '#1B2A6B', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const ghostBtnStyle   = { flex: 1, padding: '11px 0', background: 'transparent', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' };
